import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import { fgPortalMount } from "../../lib/fgPortalMount";
import "./ui.css";

/**
 * Generic autocomplete input + dropdown. Replaces the prototype's three
 * near-identical lookups — `CityAutocomplete`, `EventLookupInput`,
 * `ProductLookup` (docs/v4/02 §12). Keyboard + outside-click handled here.
 */
export function Autocomplete<T>({
  value,
  onChange,
  getOptions,
  optionKey,
  renderOption,
  onPick,
  placeholder,
  ariaLabel,
  footerLabel,
  onFooterClick,
  inputStyle,
  menuPortal = false
}: {
  /** Input text — controlled by the caller. */
  value: string;
  onChange: (text: string) => void;
  /** Derive the visible options from the current query. */
  getOptions: (query: string) => T[];
  optionKey: (option: T) => string;
  renderOption: (option: T) => ReactNode;
  onPick: (option: T) => void;
  placeholder?: string;
  /** Accessible name for the input — placeholder alone doesn't name a combobox. */
  ariaLabel?: string;
  /** Optional sticky footer action, e.g. "+ Add a custom event". */
  footerLabel?: string;
  onFooterClick?: (query: string) => void;
  inputStyle?: CSSProperties;
  /**
   * Render the dropdown in a fixed-position portal (above the modal scrim)
   * instead of as an absolutely-positioned child. Set this when the field
   * lives inside an `overflow: hidden` container (e.g. `ModalShell`) that
   * would otherwise clip the menu. Mirrors the `EventSearchField` floating
   * panel pattern.
   */
  menuPortal?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuGeo, setMenuGeo] = useState({ top: 0, left: 0, width: 0 });

  const options = getOptions(value);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      // The portaled menu lives outside `ref`, so check it explicitly —
      // otherwise a click on an option reads as an outside-click and closes
      // the menu before the option's `onClick` fires.
      if (ref.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  // Measure the input wrapper so the portaled menu can anchor to it with
  // `position: fixed`. Only runs in portal mode while open.
  const measureMenu = useCallback(() => {
    const wrap = ref.current;
    if (!wrap || typeof window === "undefined") return;
    const r = wrap.getBoundingClientRect();
    const pad = 8;
    const width = Math.max(1, Math.round(r.width));
    let left = Math.round(r.left);
    if (left + width > window.innerWidth - pad) {
      left = Math.max(pad, window.innerWidth - pad - width);
    }
    if (left < pad) left = pad;
    // Flip above the field when there isn't room below (menu is ~280px tall).
    const estMenuH = 280;
    const spaceBelow = window.innerHeight - r.bottom - pad;
    let top = Math.round(r.bottom + 4);
    if (spaceBelow < estMenuH * 0.38 && r.top - pad > spaceBelow) {
      top = Math.max(pad, Math.round(r.top - estMenuH - 4));
    }
    setMenuGeo({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!menuPortal || !open) return;
    measureMenu();
    const wrap = ref.current;
    const ro =
      typeof ResizeObserver !== "undefined" && wrap
        ? new ResizeObserver(() => measureMenu())
        : null;
    if (wrap && ro) ro.observe(wrap);
    window.addEventListener("scroll", measureMenu, true);
    window.addEventListener("resize", measureMenu);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", measureMenu, true);
      window.removeEventListener("resize", measureMenu);
    };
  }, [menuPortal, open, measureMenu, options.length]);

  const pick = (opt: T) => {
    onPick(opt);
    setOpen(false);
    setActiveIdx(-1);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(options.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && open && activeIdx >= 0 && options[activeIdx]) {
      e.preventDefault();
      pick(options[activeIdx]);
    }
  };

  const showMenu = open && (options.length > 0 || Boolean(footerLabel));
  const menu = showMenu ? (
    <div
      ref={menuRef}
      className={`fgx-ac__menu${menuPortal ? " fgx-ac__menu--portal" : ""}`}
      role="listbox"
      style={
        menuPortal
          ? { top: menuGeo.top, left: menuGeo.left, width: menuGeo.width }
          : undefined
      }>
      {options.map((opt, i) => (
        <button
          key={optionKey(opt)}
          type="button"
          role="option"
          aria-selected={i === activeIdx}
          className={`fgx-ac__opt${i === activeIdx ? " is-active" : ""}`}
          onMouseDown={(e) => e.preventDefault()}
          onMouseEnter={() => setActiveIdx(i)}
          onClick={() => pick(opt)}>
          {renderOption(opt)}
        </button>
      ))}
      {footerLabel && (
        <button
          type="button"
          className="fgx-ac__footer"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onFooterClick?.(value);
            setOpen(false);
          }}>
          {footerLabel}
        </button>
      )}
    </div>
  ) : null;

  const portalTarget =
    typeof document !== "undefined" ? fgPortalMount() ?? document.body : null;

  return (
    <div ref={ref} className="fgx-ac">
      <input
        className="fg-input"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        style={inputStyle}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIdx(-1);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {menuPortal ? menu && portalTarget && createPortal(menu, portalTarget) : menu}
    </div>
  );
}
